/**
 * Feature hook: CheckinDocument state (flattened from nested slices) and doc-man check-in thunks.
 * Do not use Redux state or dispatch directly in components; use this hook instead.
 */
import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../hooks";
import { selectCheckinFlat } from "../slices/checkin";
import { checkinDocumentThunk } from "../slices/checkinApi";
import {
  onCheckinExistingDocument,
  onCheckinNewRevision,
  onCheckinViewCopy,
  getRecentObjects,
  onObjectConnectionsLoad,
  onBasicDataLoad,
  onGetFormatsByClass,
  onGetObjectConnectionColumns,
  onNewRevisionInfo,
} from "../slices/docManAction";

export function useCheckin() {
  const dispatch = useAppDispatch();
  const checkinState = useAppSelector(selectCheckinFlat);

  const onCheckinDocumentsAction = useCallback(
    (
      docTitle,
      docClass,
      docFormat,
      uploadFiles,
      createViewCopy,
      selectedObjects,
      uploadViewCopyCount,
      viewRequired,
      docReleaseStatus,
      oneDriveDeleteRequired,
      tokenRefreshPreAction
    ) =>
      dispatch(
        checkinDocumentThunk({
          docTitle,
          docClass,
          docFormat,
          uploadFiles,
          createViewCopy,
          selectedObjects,
          uploadViewCopyCount,
          viewRequired,
          docReleaseStatus,
          oneDriveDeleteRequired,
          tokenRefreshPreAction,
        })
      ),
    [dispatch]
  );
  const onCheckinExistingDocumentAction = useCallback(
    (authChannel, payload, tokenRefreshPreAction) =>
      dispatch(onCheckinExistingDocument(authChannel, payload, tokenRefreshPreAction)),
    [dispatch]
  );
  const onCheckinNewRevisionAction = useCallback(
    (authChannel, payload, tokenRefreshPreAction) =>
      dispatch(onCheckinNewRevision(authChannel, payload, tokenRefreshPreAction)),
    [dispatch]
  );
  const getRecentObjectsAction = useCallback(
    (tokenRefreshPreAction) => dispatch(getRecentObjects(tokenRefreshPreAction)),
    [dispatch]
  );
  const onObjectConnectionsLoadAction = useCallback(
    (teamsContext, tokenRefreshPreAction) =>
      dispatch(onObjectConnectionsLoad(teamsContext, tokenRefreshPreAction)),
    [dispatch]
  );
  const onBasicDataLoadAction = useCallback(
    (authChannel, luName, viewName, tokenRefreshPreAction) =>
      dispatch(onBasicDataLoad(authChannel, luName, viewName, tokenRefreshPreAction)),
    [dispatch]
  );
  const onGetObjectConnectionColumnsAction = useCallback(
    (authChannel, luName, viewName, tokenRefreshPreAction) =>
      dispatch(onGetObjectConnectionColumns(authChannel, luName, viewName, tokenRefreshPreAction)),
    [dispatch]
  );
  const onGetFormatsByClassAction = useCallback(
    (className) => dispatch(onGetFormatsByClass(className)),
    [dispatch]
  );
  const onNewRevisionInfoAction = useCallback(
    (rowSelectionDetails, uploadFiles, uploadViewCopyCount, requestDocRevInfo, documentCheckInType, tokenRefreshPreAction, actionType) =>
      dispatch(onNewRevisionInfo(rowSelectionDetails, uploadFiles, uploadViewCopyCount, requestDocRevInfo, documentCheckInType, tokenRefreshPreAction, actionType)),
    [dispatch]
  );
  const onCheckinViewCopyAction = useCallback(
    (docTitle, docClass, docNo, DocSheet, docRev, viewCopyFiles, viewRequired, docReleaseStatus, checkinType, deleteOriginal, tokenRefreshPreAction, actionType) =>
      dispatch(onCheckinViewCopy(docTitle, docClass, docNo, DocSheet, docRev, viewCopyFiles, viewRequired, docReleaseStatus, checkinType, deleteOriginal, tokenRefreshPreAction, actionType)),
    [dispatch]
  );

  return {
    // Checkin state (flattened)
    ...checkinState,
    // Actions (useCallback-wrapped)
    onCheckinDocuments: onCheckinDocumentsAction,
    onCheckinExistingDocument: onCheckinExistingDocumentAction,
    onCheckinNewRevision: onCheckinNewRevisionAction,
    getRecentObjects: getRecentObjectsAction,
    onObjectConnectionsLoad: onObjectConnectionsLoadAction,
    onBasicDataLoad: onBasicDataLoadAction,
    onGetFormatsByClass: onGetFormatsByClassAction,
    onGetObjectConnectionColumns: onGetObjectConnectionColumnsAction,
    onNewRevisionInfo: onNewRevisionInfoAction,
    onCheckinViewCopy: onCheckinViewCopyAction,
  };
}
